import React, { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Collapse, CircularProgress } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckIcon from '@mui/icons-material/Check';
import { ChannelType } from '../../../../../services/useChannels';
import {
  ChannelCard,
  ChannelCardHeader,
  ChannelCardContent,
  ChannelInfo,
  ChannelIcon,
  StatusChip,
  ExpandButton,
  UnitsList,
  TableHeader,
  TableHeaderCell,
} from '../../../styles/UnitsMappingStyles';
import { ChannelConfig, UnitData, ChannelName } from '../../../types';
import FilterSection from '../FilterSection';
import UnitRowItem from '../UnitRowItem';

// Import unified channel logo components
import { ChannelLogo, ChannelLogoType } from '../../ChannelLogos';

// Helper function to get channel logo type
const getChannelLogoType = (channelType: ChannelType): ChannelLogoType | null => {
  switch (channelType) {
    case ChannelType.Airbnb:
      return 'airbnb';
    case ChannelType.Booking:
      return 'booking';
    case ChannelType.Gathern:
      return 'gathern';
    default:
      return null;
  }
};

interface ChannelCardComponentProps {
  channelKey: string;
  config: ChannelConfig;
  units: UnitData[];
  filteredUnits: UnitData[];
  isExpanded: boolean;
  isLoading: boolean;
  searchQuery: string;
  statusFilter: string;
  selectedCount: number;
  onToggleExpand: (channelKey: string) => void;
  onSearchChange: (channelKey: string, query: string) => void;
  onStatusFilterChange: (channelKey: string, status: string) => void;
  onUnitToggle: (unit: UnitData, channelKey: ChannelName) => void;
  isUnitSelected: (unitId: number, channel: ChannelName) => boolean;
  disabled?: boolean;
}

const ChannelCardComponent: React.FC<ChannelCardComponentProps> = memo(({
  channelKey,
  config,
  units,
  filteredUnits,
  isExpanded,
  isLoading,
  searchQuery,
  statusFilter,
  selectedCount,
  onToggleExpand,
  onSearchChange,
  onStatusFilterChange,
  onUnitToggle,
  isUnitSelected,
  disabled = false,
}) => {
  const { t } = useTranslation();

  const handleSearchChange = useCallback((query: string) => {
    onSearchChange(channelKey, query);
  }, [channelKey, onSearchChange]);

  const handleStatusFilterChange = useCallback((status: string) => {
    onStatusFilterChange(channelKey, status);
  }, [channelKey, onStatusFilterChange]);

  const handleToggleExpand = useCallback(() => {
    onToggleExpand(channelKey);
  }, [channelKey, onToggleExpand]);

  return (
    <ChannelCard sx={{ opacity: disabled ? 0.5 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>
      <ChannelCardHeader onClick={disabled ? undefined : handleToggleExpand} sx={{ cursor: disabled ? 'not-allowed' : 'pointer' }}>
        <ChannelInfo>
          <ChannelIcon sx={{ bgcolor: 'transparent', padding: '0' }}>
            {getChannelLogoType(config.channelType) && (
              <ChannelLogo channel={getChannelLogoType(config.channelType)!} size="sm" rounded />
            )}
          </ChannelIcon>
          <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#1a1a1a' }}>
            {config.name} ({units.length})
          </Typography>
          {selectedCount > 0 && (
            <StatusChip
              label={`${t('CHANNELS.UNITS_MAPPING.SELECTED', 'تم الاختيار')}`}
              size="small"
              icon={<CheckIcon sx={{ fontSize: 14 }} />}
            />
          )}
        </ChannelInfo>
        <ExpandButton>
          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </ExpandButton>
      </ChannelCardHeader>

      <Collapse in={isExpanded}>
        <ChannelCardContent>
          <FilterSection
            searchQuery={searchQuery}
            statusFilter={statusFilter}
            onSearchChange={handleSearchChange}
            onStatusFilterChange={handleStatusFilterChange}
          />

          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
              <CircularProgress size={24} sx={{ color: '#5b3fff' }} />
            </Box>
          ) : filteredUnits.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
              <Typography sx={{ color: '#666', fontFamily: '"IBM Plex Sans Arabic", sans-serif' }}>
                {t('CHANNELS.UNITS_MAPPING.NO_UNITS', 'لا توجد وحدات متاحة')}
              </Typography>
            </Box>
          ) : (
            <UnitsList>
              <TableHeader>
                <TableHeaderCell sx={{ width: '600px' }}>{t('CHANNELS.UNITS_MAPPING.PROPERTY', 'العقار')}</TableHeaderCell>
                <TableHeaderCell sx={{ width: '240px' }}>{t('CHANNELS.TABLE_ID', 'الرقم التعريفي')}</TableHeaderCell>
                <TableHeaderCell sx={{ width: '240px' }}>{t('CHANNELS.TABLE_STATUS', 'الحالة')}</TableHeaderCell>
              </TableHeader>
              {filteredUnits.map((unit) => {
                const isSelected = isUnitSelected(unit.id, channelKey as ChannelName);
                const isMapped = unit.isMapped === true;

                return (
                  <UnitRowItem
                    key={unit.id}
                    unit={unit}
                    channelKey={channelKey as ChannelName}
                    isSelected={isSelected}
                    isMapped={isMapped}
                    onToggle={onUnitToggle}
                  />
                );
              })}
            </UnitsList>
          )}
        </ChannelCardContent>
      </Collapse>
    </ChannelCard>
  );
});

ChannelCardComponent.displayName = 'ChannelCardComponent';

export default ChannelCardComponent;
